import "../dataset-style.scss";

import { CAlert, CButton, CCardHeader, CSwitch } from "@coreui/react";
import { Link, useParams } from "react-router-dom";
import { useMemo, useState } from "react";

import DatasetInfo from "../DatasetInfo";
import { PropTypes } from "prop-types";
import { useDropzone } from "react-dropzone";

const baseStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "20px",
  borderWidth: 2,
  borderRadius: 2,
  borderColor: "#e6e6e6",
  borderStyle: "dashed",
  backgroundColor: "#fafafa",
  color: "#b0b0b0",
  outline: "none",
  transition: "border .24s ease-in-out",
};

const focusedStyle = {
  borderColor: "#2196f3",
};

const acceptStyle = {
  borderColor: "#00e676",
};

const rejectStyle = {
  borderColor: "#ff1744",
};

const FileAcceptedRow = ({ file }) => {
  return (
    <tr className="file-row">
      <td className="file-cell">
        <strong>{file.props.children}</strong> accepted
      </td>
    </tr>
  );
};

// validate props for fileacceptedrow
FileAcceptedRow.propTypes = {
  file: PropTypes.object,
};

function StyledDropzone(props) {
  const [fileUploadSuccess, setFileUploadSuccess] = useState(false);
  const [isBigFile, setIsBigFile] = useState(false);

  const {
    acceptedFiles,
    fileRejections,
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    // stupid windows only puts out an excel type, even for .csvs and .csv or mimetype does not work
    multiple: props.acceptsMultipleFiles,
    validator: () => {},
    onDropAccepted: (files) => {
      // Returns true if at least one file is too big
      const fileTooBig = files.some((file) => file.size / 1024 > 5120);
      setIsBigFile(fileTooBig);
      setFileUploadSuccess(true);
      props.onDragSuccess(props.acceptsMultipleFiles ? files : files[0]);
    },
    onDropRejected: (files) => {
      props.onDragFailed(files);
    },

    accept: props.acceptTypeString,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject],
  );

  const files = acceptedFiles.map((file) => (
    <span key={file.path}>{file.name}</span>
  ));

  return (
    <div className="container">
      <span>{isDragReject}</span>
      <div
        {...getRootProps({ style })}
        id="csv-dropzone"
        data-testid="ds-upload-dropzone"
        data-attr="datasets:form:file-dropzone"
      >
        <input {...getInputProps()} data-testid="ds-upload-input" />
        <h4>{props.titleMessage}</h4>
        <p></p>
        <p>{props.detailMessage}</p>
      </div>

      {/* If Success */}

      <div style={{ marginTop: "1em" }}>
        {props.showFileAcceptedMessage &&
          fileUploadSuccess &&
          files.length > 0 && (
            <CAlert color="success" data-testid="ds-upload-alert-success">
              {files.map((file) => {
                return <FileAcceptedRow key={file} file={file} />;
              })}
            </CAlert>
          )}

        {/* Reject */}
        {props.showFileRejectionMessage && fileRejections.length > 0 && (
          <div>
            <CAlert color="danger" data-testid="ds-upload-alert-reject">
              {fileRejections[0].file.path} has failed validation.
              <p></p>
              {fileRejections[0].errors.map((error) => {
                return (
                  <div key={error.code}>
                    <b>Error:</b> {error.code}
                    <br></br>
                    <b>Error Message: </b> {error.message}
                  </div>
                );
              })}
            </CAlert>
          </div>
        )}

        {props.showFileRejectionMessage && isBigFile && (
          <div>
            <CAlert color="warning" data-testid="ds-upload-alert-warning">
              <strong>Warning:</strong> File size is too large to confirm that
              it is UTF-8 encoded. Please ensure the file is UTF-8 encoded
              before continuing.
            </CAlert>
          </div>
        )}
      </div>
    </div>
  );
}

StyledDropzone.propTypes = {
  onDragSuccess: PropTypes.func,
  onDragFailed: PropTypes.func,
  isBigFile: PropTypes.bool,
  onBigFile: PropTypes.func,
  acceptTypeString: PropTypes.string,
  titleMessage: PropTypes.string,
  detailMessage: PropTypes.string,
  acceptsMultipleFiles: PropTypes.bool,
  showFileAcceptedMessage: PropTypes.bool,
  showFileRejectionMessage: PropTypes.bool,
};

StyledDropzone.default = {
  isBigFile: false,
  acceptTypeString: ".csv, application/vnd.ms-excel, text/csv, .pdf, .xlsx",
  showFileAcceptedMessage: true,
  showFileRejectionMessage: true,
};

export default StyledDropzone;

export const UploadToDatasetHeader = ({
  showAutoUploadSwitch = false,
  isAutoUpload = true,
  handleIsAutoUpload,
}) => {
  const { dataset_id } = useParams();

  return (
    <CCardHeader>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ alignSelf: "left" }}>
          <DatasetInfo
            dataset_id={dataset_id}
            just_name_and_description={true}
          />
        </div>
        <div className="d-flex align-items-center">
          <Link to={`/datasets/${dataset_id}/viewer`} className="mr-3">
            <CButton
              color="primary"
              size="sm"
              data-attr="datasets:btn:view-dataset-from-upload"
            >
              View Dataset
            </CButton>
          </Link>
          <div
            style={{
              fontSize: "1em",
              display: "flex",
              alignContent: "baseline",
              visibility: !showAutoUploadSwitch,
            }}
          >
            <div style={{ marginTop: "0px" }}>AutoUpload</div>
            <div style={{ justifyContent: "center", marginLeft: "1em" }}>
              <CSwitch
                id="autoupload"
                data-testid="ds-upload-switch-auto"
                data-attr="datasets:toggle:auto-upload"
                name="autoupload-switch"
                color={"info"}
                size={"sm"}
                checked={isAutoUpload}
                label="AutoUpload"
                labelOn="Yes"
                labelOff="No"
                onChange={(e) => {
                  handleIsAutoUpload(e.target.checked);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </CCardHeader>
  );
};

UploadToDatasetHeader.propTypes = {
  showAutoUploadSwitch: PropTypes.bool,
  isAutoUpload: PropTypes.bool,
  handleIsAutoUpload: PropTypes.func,
};
